import React, { useMemo } from "react";

import TableFilters from "components/TableFilters";
import {
  DateRangeType,
  FilterTypeEnum,
  FilterValue,
  TableFilter,
} from "components/TableFilters/types";
import { InvoiceFiltersState } from "../InvoiceFilters/types";

import { ReactComponent as StarIcon } from "assets/images/icon-star.svg";

import { useAppSelector } from "store/hooks";

import { dateOptions, expirationOptions } from "./filterOptions";

interface InvoiceFilterProps {
  activeFilters: InvoiceFiltersState;
  handleFilterChange: (
    filterId: keyof InvoiceFiltersState,
    activeFilter: FilterValue
  ) => void;
  onClearFilters: () => void;
}

const InvoiceFilters: React.FC<InvoiceFilterProps> = ({
  activeFilters,
  handleFilterChange,
  onClearFilters,
}) => {
  const accounts = useAppSelector((state) => state.ibanAccount.accounts);

  const ibanOptions = useMemo(
    () =>
      accounts
        ? Array.from(new Set(accounts.map((account) => account.iban)))
        : [],
    [accounts]
  );

  const identificationCodeOptions = useMemo(
    () =>
      accounts
        ? Array.from(
            new Set(accounts.map((account) => account.identificationCode))
          )
        : [],
    [accounts]
  );

  const filters: TableFilter<InvoiceFiltersState>[] = [
    {
      id: "favorite",
      label: "Обрані",
      type: FilterTypeEnum.TOGGLE,
      children: <StarIcon style={{ marginRight: "4px" }} />,
      extractValues: () => [],
      removeValue: () => [],
    },
    {
      id: "ibans",
      label: "IBAN",
      options: ibanOptions,
      emptyText: "Створіть посилання для використання фільтра",
      type: FilterTypeEnum.CHECKBOX,
      extractValues: (ibans) => ibans || [],
      removeValue: (ibans, value) =>
        (ibans || []).filter((iban) => iban !== value),
    },
    {
      id: "identificationCodes",
      label: "ЕДРПОУ",
      options: identificationCodeOptions,
      emptyText: "Створіть посилання для використання фільтра",
      type: FilterTypeEnum.CHECKBOX,
      extractValues: (codes) => codes || [],
      removeValue: (codes, value) =>
        (codes || []).filter((code) => code !== value),
    },
    {
      id: "invoiceDateRange",
      label: "Дата",
      options: dateOptions,
      type: FilterTypeEnum.DATE_RANGE,
      extractValues: (date) => {
        const { id, range } = date;

        const option = dateOptions.find((opt) => opt.id === id);

        if (!option || option.type === DateRangeType.ALL_TIME) {
          return [];
        }

        if (option.type === DateRangeType.CUSTOM_PERIOD) {
          return range?.from && range?.to
            ? [
                `${range.from.format("DD.MM.YYYY")} - ${range.to.format(
                  "DD.MM.YYYY"
                )}`,
              ]
            : [];
        }

        return [option.title];
      },
      removeValue: () => ({ id: "all_time", range: null }),
    },
    {
      id: "expirationDateRange",
      label: "Термін дії",
      options: expirationOptions,
      type: FilterTypeEnum.DATE_RANGE,
      extractValues: (expiration) => {
        const { id, range } = expiration;

        const option = expirationOptions.find((opt) => opt.id === id);

        if (!option || option.type === DateRangeType.ALL_TIME) {
          return [];
        }

        if (option.type === DateRangeType.CUSTOM_PERIOD) {
          return range?.from && range?.to
            ? [
                `${range.from.format("DD.MM.YYYY")} - ${range.to.format(
                  "DD.MM.YYYY"
                )}`,
              ]
            : [];
        }
        return [option.title];
      },
      removeValue: () => ({ id: "all_time", range: null }),
    },
  ];

  return (
    <>
      <TableFilters<InvoiceFiltersState>
        filters={filters}
        activeFilters={activeFilters}
        onClearFilters={onClearFilters}
        onFilterChange={handleFilterChange}
      />
    </>
  );
};

export default InvoiceFilters;
